import type { PuntoTasa, RegistroTasa } from "./tipos"

/**
 * Las tasas en pantalla: «832,49», con los separadores de aquí y redondeo exacto.
 *
 * ⚠ **Se redondea sobre el TEXTO decimal, con `bigint`, y no con `toFixed()`.** En coma
 * flotante 1,005 no existe —es 1,00499…— y `toFixed(2)` da «1,00». El servidor manda
 * las tasas como texto (son `Decimal`) y aquí se redondean a la mitad hacia arriba sin
 * pasar por un `Number`: el precio del dólar es justo la cifra que alguien copia a mano
 * en una factura.
 */

const DECIMAL = /^([+-])?(\d+)(?:\.(\d+))?$/

/** «832.488300» a «832,49», o con los decimales que se pidan. */
export function comoTasa(texto: string, decimales = 2): string {
  const partes = DECIMAL.exec(texto.trim())
  if (partes === null) return texto
  const [, signo, entero = "0", fraccion = ""] = partes
  const valor = BigInt(entero) * 10n ** BigInt(fraccion.length) + BigInt(fraccion || "0")

  let unidades: bigint
  if (fraccion.length <= decimales) {
    unidades = valor * 10n ** BigInt(decimales - fraccion.length)
  } else {
    const divisor = 10n ** BigInt(fraccion.length - decimales)
    unidades = valor / divisor
    if ((valor % divisor) * 2n >= divisor) unidades += 1n
  }

  const potencia = 10n ** BigInt(decimales)
  const parteEntera = (unidades / potencia).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
  const parteDecimal =
    decimales > 0 ? `,${(unidades % potencia).toString().padStart(decimales, "0")}` : ""
  // Un «-0,00» diría que algo bajó cuando no se movió.
  const negativo = signo === "-" && unidades !== 0n
  return `${negativo ? "-" : ""}${parteEntera}${parteDecimal}`
}

export type Sentido = "sube" | "baja" | "igual"

/** Hacia dónde se movió, a la vista de sus dos decimales. */
export function sentidoDe(variacion: string | null): Sentido | null {
  if (variacion === null) return null
  const redondeada = comoTasa(variacion, 2)
  if (!/^-?[\d.]+,\d+$/.test(redondeada)) return null
  if (/^-?0,00$/.test(redondeada)) return "igual"
  return redondeada.startsWith("-") ? "baja" : "sube"
}

/** «+4,06 %», «−0,22 %» o «0,00 %». El menos es el tipográfico, que alinea con el más. */
export function comoVariacion(variacion: string): string {
  const redondeada = comoTasa(variacion, 2)
  const sentido = sentidoDe(variacion)
  if (sentido === "baja") return `−${redondeada.slice(1)} %`
  if (sentido === "sube") return `+${redondeada} %`
  return `${redondeada} %`
}

/** «12/09», para los ejes y las comparaciones cortas. */
export function diaYMes(fecha: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(fecha)
  return m === null ? fecha : `${m[2]}/${m[1]}`
}

/**
 * El cierre de cada día, del más viejo al más nuevo, para el gráfico.
 *
 * Los registros llegan del más reciente al más antiguo, así que el PRIMERO de cada
 * fecha es su cierre. Los que no tienen bolívares —sin dólar con qué convertir— no
 * cuentan: un hueco en la línea es verdad; un cero, no.
 */
export function cierresDiarios(registros: readonly RegistroTasa[]): PuntoTasa[] {
  const vistos = new Set<string>()
  const salida: PuntoTasa[] = []
  for (const r of registros) {
    if (r.bs_por_unidad === null || vistos.has(r.fecha_valor)) continue
    vistos.add(r.fecha_valor)
    salida.push({ fecha_valor: r.fecha_valor, bs_por_unidad: r.bs_por_unidad })
  }
  return salida.reverse()
}
